import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-enterprise";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_RCV_REG_012 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  const isFirstRender = useRef(true); // 조회조건 전표유형 관련 로직이 돌았는지
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "1", "", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  // 전표유형 조회조건 바뀌면 출고형태, 보이는 컬럼 변경
  const onSearchPanelValueChanged = (event: ValueChangedEvent) => {
    const { colId, newValue } = event;

    switch (colId) {
      case "tp_slip":
        isFirstRender.current = false;
        const virtual = setVisibleColums(columns.grid1, newValue);
        getGridCurrent("grid1").changeColumnReorderConfigs(virtual);
        break;
    }
  };

  const setVisibleColums = (gridColumns: any[], tpSlip: string) => {
    const columns: any[] = [];

    gridColumns.forEach((row: any) => {
      switch (row.cd_column) {
        case "cd_io":
          const cdIoList: any[] = [];

          iotypeRef.current.forEach((item) => {
            if (item.tp_slip === tpSlip) {
              cdIoList.push({ code: item.cd_io, name: item.nm_io });
            }
          });

          const jsonResult = JSON.stringify(cdIoList, null);
          columns.push({
            cd_column: row.cd_column,
            fg_visible: "Y",
            no_seq: row.no_seq,
            js_combo: jsonResult,
          });
          break;
        case "tp_trans":
          if (tpSlip === "6") {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "Y",
              no_seq: row.no_seq,
            });
          } else {
            columns.push({
              cd_column: row.cd_column,
              fg_visible: "N",
              no_seq: row.no_seq,
            });
          }
          break;
        default:
          columns.push({
            cd_column: row.cd_column,
            fg_visible: row.fg_visible,
            no_seq: row.no_seq,
          });
          break;
      }
    });

    return [...columns];
  };

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_rcv_reg_012_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const searchInfo = searchRef.current.getFormdata();

      if (searchInfo.tp_slip === undefined || searchInfo.tp_slip === "") {
        showDialog("alter", "전표유형은 필수입니다.");
        return;
      }
      // 첫 렌더링 안돌아서 이렇게
      if (isFirstRender.current) {
        isFirstRender.current = false;
        const virtual = setVisibleColums(columns.grid1, "3");
        getGridCurrent("grid1").changeColumnReorderConfigs(virtual);
      }

      // cd_io top1로직
      const validItems = iotypeRef.current.filter((item) => {
        if (item.tp_slip !== searchInfo.tp_slip) return false;
        return item;
      });

      const cdIoTop1 = validItems.sort((a, b) =>
        a.cd_io.localeCompare(b.cd_io)
      )[0]?.cd_io;

      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", cdIoTop1);
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }
    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_req: nodeData.no_req,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const rcvreqhChanges = getGridCurrent("grid1").getChange();
      const rcvreqlChanges = getGridCurrent("grid2").getChange();

      const headerInserts = rcvreqhChanges.insert ?? [];
      const lineInserts = rcvreqlChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        rcvreqhChanges.modifiedRowcount === 0 &&
        rcvreqlChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (rcvreqhChanges.insert.length > 0) {
        const data = rcvreqhChanges.insert[0];
        switch (data.tp_slip) {
          case "6":
            if (converter.getString(data.tp_trans) === "") {
              await showDialog("alter", "대체유형은 필수입니다.");
              return false;
            }
            break;
        }
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_req",
        cd_module: "MTL",
        no_line: 25,
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [rcvreqhChanges, rcvreqlChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        rcvreqh: rcvreqhChanges,
        rcvreql: rcvreqlChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_RCV_REG_012_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_req: today,
        fg_slip: "2",
        tp_slip: searchInfo?.tp_slip,
        st_req: "F",
        no_req: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");

        if (rowCount > 0) {
          const stReq = ioType.yn_app === "Y" ? "P" : "F";

          gridRefs.current["grid2"].current?.forEachFilteredNode(
            (node: RowNode) => {
              node.setDataValue("st_req", stReq, "noEvent");
            }
          );
        }
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("cd_wh", rowData.cd_wh, "noEvent");
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_req);
    } else {
      return;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      if (focusedRowNodeH.data.st_req === "C") {
        showDialog("alter", "이미 마감된 건입니다.");
        return false;
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        no_line: maxNoLine + 1,
        no_req: focusedRowNodeH.data?.no_req,
        dt_dlv: today,
        qt: 0,
        qt_io: 0,
        cd_wh: focusedRowNodeH.data?.cd_wh,
        st_req: "F",
        qt_inv: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;

    if (colId === "cd_pjt") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_pjt) return;

      let apply = false;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (converter.getString(node.data[colId]) === "") {
          apply = true;
        }
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (converter.getString(node.data.cd_pjt) === "") {
            node.setDataValue("cd_pjt", rowData.cd_pjt);
            node.setDataValue("nm_pjt", rowData.nm_pjt);
          }
        });
      }
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "입고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_RCV_REG_012;
